// src/modules/units/tyres/views/archive/archivedtyres.view.js
//
// Pure rendering for the Archived tyres list — every function takes data and
// returns an HTML string (or a plain value), with no fetching or DOM side
// effects, so it runs under Node (archivedtyres-view.harness.mjs) and the
// orchestrator (archivedtyres.js) stays a thin fetch-and-swap shell.
//
// Layout reuses the register's primitives (tm-register / tm-toolbar / tm-search /
// tm-table / tm-footer / tm-pager) so an archived row reads like a register row.
// Rows are NOT clickable: the detail endpoints hide archived tyres, so there is no
// detail to open until the tyre is restored. The one thing you can do with an
// archived tyre is restore it, so that is the row's only control.

import { esc, formatDateTime, formatCount } from '../../shared/tyre-format.js';
import { icon } from '../../shared/tyre-icons.js';
import { tyreStatusLabel, tyreStatusTone, hasKnownStatus } from '../../shared/tyre-status.js';

const MUTED = '<span class="tm-muted">—</span>';
const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const cell = (v) => (blank(v) ? MUTED : esc(v));

export const ARCHIVED_COLUMNS = Object.freeze([
  'Tyre ID', 'Last status', 'Tyre', 'Yard', 'Archived', 'Archived by', ''
]);
const COLSPAN = ARCHIVED_COLUMNS.length;

// "Michelin Multi HD · 315/80/R22.5" — one cell that identifies the tyre without
// four sparse columns. Brand falls back to tyreMake for older records.
export function tyreSummary(tyre) {
  const name = [tyre?.tyreBrand || tyre?.tyreMake, tyre?.tyreModel]
    .filter((v) => !blank(v)).join(' ');
  const size = blank(tyre?.tyreSize) ? '' : String(tyre.tyreSize);
  return [name, size].filter(Boolean).join(' · ');
}

function statusBadge(tyre) {
  if (!hasKnownStatus(tyre)) return MUTED;
  return `<span class="tm-badge tm-tone-${esc(tyreStatusTone(tyre))}">${esc(tyreStatusLabel(tyre))}</span>`;
}

/**
 * One archived tyre.
 * @param {object} tyre row from GET /api/tyre/deleted
 * @param {{ restoringId?: string|null, canRestore?: boolean }} [opts]
 *   restoringId — the tyre currently being restored (its button shows progress;
 *   every other Restore button is disabled so two restores never race).
 */
export function rowHTML(tyre, { restoringId = null, canRestore = true } = {}) {
  const id = String(tyre?.externalTyreId ?? '');
  const isThis = restoringId !== null && restoringId === id;
  const anyBusy = restoringId !== null;
  const btn = canRestore && id
    ? `<button type="button" class="tm-btn-restore" data-restore="${esc(id)}"` +
      `${anyBusy ? ' disabled' : ''} aria-label="Restore tyre ${esc(id)}">` +
      `${icon('restore')}<span>${isThis ? 'Restoring…' : 'Restore'}</span></button>`
    : '';
  return `
    <tr class="tm-row tm-archived-row" data-id="${esc(id)}">
      <td><b>${cell(id)}</b></td>
      <td>${statusBadge(tyre)}</td>
      <td>${cell(tyreSummary(tyre))}</td>
      <td>${cell(tyre?.yardName)}</td>
      <td>${blank(tyre?.deletedAt) ? MUTED : esc(formatDateTime(tyre.deletedAt))}</td>
      <td>${cell(tyre?.deletedBy)}</td>
      <td class="tm-archived-actions">${btn}</td>
    </tr>`;
}

// One state row (loading / empty / error / denied) spanning the table. The
// orchestrator wires [data-retry] and [data-clear].
export function stateRowHTML(kind, message, { retry = false, clear = false } = {}) {
  const ic = kind === 'empty' ? 'archive' : kind === 'loading' ? 'find' : 'reports';
  const extra =
    (retry ? ' <button type="button" class="tm-retry" data-retry>Retry</button>' : '') +
    (clear ? ' <button type="button" class="tm-retry" data-clear>Clear search</button>' : '');
  return `
    <tr class="tm-state-tr">
      <td class="tm-state-td" colspan="${COLSPAN}">
        <div class="tm-state">
          <span class="tm-state-icon">${icon(ic)}</span>
          <span class="tm-state-msg">${esc(message)}${extra}</span>
        </div>
      </td>
    </tr>`;
}

/**
 * Keep only the archived tyres booked into the selected yard. `yardId` null means
 * All yards (tyre-scope.js), which keeps everything. Compared as strings because
 * the yard object's `Id` and a tyre's `yardId` are not guaranteed the same type.
 * A tyre with no yard is excluded from any specific yard, matching how the
 * server's ?yardId= filter treats the live register.
 */
export function filterArchivedByYard(rows, yardId) {
  const list = Array.isArray(rows) ? rows : [];
  if (yardId === null || yardId === undefined || yardId === '') return list.slice();
  const want = String(yardId);
  return list.filter((t) => t?.yardId !== null && t?.yardId !== undefined && String(t.yardId) === want);
}

/**
 * Newest archive first, so what was just archived is on top. Rows without a
 * deletedAt sink to the bottom. Returns a new array.
 */
export function sortArchivedForDisplay(rows) {
  const time = (t) => {
    const n = Date.parse(t?.deletedAt ?? '');
    return Number.isFinite(n) ? n : -Infinity;
  };
  return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => time(b) - time(a));
}

/**
 * One page of an already-filtered list, plus the footer facts. Paging is on the
 * client because the yard filter is: server paging would hand back pages the
 * filter then thins out (a "page" of 3, or an empty page with a Next button).
 * An offset past the end snaps back to the last page.
 *
 * @returns {{ rows:object[], offset:number, from:number, to:number, total:number, hasPrev:boolean, hasNext:boolean }}
 */
export function clientPage(all, offset, limit) {
  const list = Array.isArray(all) ? all : [];
  const total = list.length;
  const size = Math.max(1, Number(limit) || 1);
  let start = Math.max(0, Number(offset) || 0);
  if (start >= total) start = total ? Math.floor((total - 1) / size) * size : 0;
  const rows = list.slice(start, start + size);
  return {
    rows,
    offset: start,
    from: rows.length ? start + 1 : 0,
    to: start + rows.length,
    total,
    hasPrev: start > 0,
    hasNext: start + rows.length < total
  };
}

export function summaryText(info) {
  if (!info || !info.to) return '';
  return `${formatCount(info.from)}–${formatCount(info.to)} of ${formatCount(info.total)} archived`;
}

/**
 * The feedback strip above the table. `restored` is the success case and carries
 * an "Open tyre" link, because the natural next step after restoring is usually
 * to look at (or re-fit) the tyre.
 */
export function noticeHTML(notice) {
  if (!notice || !notice.text) return '';
  const kind = notice.kind === 'ok' ? 'ok' : notice.kind === 'warn' ? 'warn' : 'error';
  const open = notice.openId
    ? ` <button type="button" class="tm-btn-link" data-open-restored="${esc(notice.openId)}">Open tyre</button>`
    : '';
  return `<div class="tm-form-msg tm-form-msg-${kind}" role="${kind === 'error' ? 'alert' : 'status'}">${esc(notice.text)}${open}</div>`;
}

// The whole screen shell. `tbodyInner` is a state row on first paint; the
// orchestrator swaps in rows, notice and footer as fetches resolve.
export function shellHTML({ search = '', tbodyInner = '', yardName = '' } = {}) {
  return `
    <div class="tm-register tm-archived">
      <div class="tm-banner tm-archived-banner">
        Archived tyres are hidden from the register, reports and exports, and their IDs cannot be reused.
        Restoring a tyre returns it to the register with its full history and last status. It does not re-fit a sensor.
      </div>
      <div class="tm-toolbar">
        <div class="tm-search">
          <span class="tm-search-icon">${icon('find')}</span>
          <input type="search" class="tm-search-input" data-search
            placeholder="Search tyre ID" aria-label="Search archived tyres by tyre ID"
            value="${esc(search)}" autocomplete="off" spellcheck="false">
        </div>
        <div class="tm-toolbar-trailing">
          <span class="tm-archived-scope" data-scope title="Follows the yard selected in the header.">${esc(yardName || 'All yards')}</span>
        </div>
      </div>

      <div data-notice aria-live="polite"></div>

      <div class="tm-table-wrap">
        <table class="tm-table">
          <thead>
            <tr>${ARCHIVED_COLUMNS.map((h) => `<th>${h ? esc(h) : '<span class="visually-hidden">Actions</span>'}</th>`).join('')}</tr>
          </thead>
          <tbody data-rows>${tbodyInner}</tbody>
        </table>
      </div>

      <div class="tm-footer">
        <span class="tm-summary" data-summary></span>
        <div class="tm-pager">
          <button type="button" class="tm-page-btn" data-prev disabled>&lsaquo; Prev</button>
          <button type="button" class="tm-page-btn" data-next disabled>Next &rsaquo;</button>
        </div>
      </div>
    </div>`;
}

export const ARCHIVED_COLSPAN = COLSPAN;
